import React from 'react';
import { Box, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import TierTableEditor, { validateTierTable, newTierTable, rateError } from './TierTableEditor';
import { getCurrencySymbol } from '../services/currencyUtils';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Server schedule -> editable draft. Missing numbers become '' so inputs stay controlled. */
export const draftFromSchedule = (schedule) => {
  const tiered = schedule?.mode === 'TIERED';
  return {
    mode: tiered ? 'TIERED' : 'FLAT',
    flatRatePercent: isNum(schedule?.flatRatePercent) ? schedule.flatRatePercent : '',
    tierCurrencyCode: schedule?.tierCurrencyCode || '',
    tiers: tiered && Array.isArray(schedule.tiers)
      ? schedule.tiers.map((t) => ({
        minVolume: t.minVolume,
        maxVolume: t.maxVolume ?? null,
        ratePercent: t.ratePercent,
      }))
      : [],
  };
};

/** Flat rate validation. `required` is true for the platform default, optional per store. */
export const validateFlatRate = (value, required) => {
  if (value === '' || value === null || value === undefined) {
    return required ? 'Enter a rate between 0 and 100' : null;
  }
  return rateError(value);
};

export const validateDraft = (draft, { flatRequired }) => {
  const flatError = validateFlatRate(draft.flatRatePercent, draft.mode === 'FLAT' || flatRequired);
  const tierResult = draft.mode === 'TIERED' ? validateTierTable(draft) : null;
  return {
    flatError,
    tierResult,
    valid: !flatError && (!tierResult || tierResult.valid),
  };
};

/** Draft -> PUT body. FLAT sends no tiers; an empty flat rate is sent as null (per-store only). */
export const draftToPayload = (draft) => {
  const flat = isNum(draft.flatRatePercent) ? draft.flatRatePercent : null;
  if (draft.mode === 'FLAT') {
    return { mode: 'FLAT', flatRatePercent: flat, tierCurrencyCode: null, tiers: [] };
  }
  return {
    mode: 'TIERED',
    flatRatePercent: flat,
    tierCurrencyCode: draft.tierCurrencyCode,
    tiers: draft.tiers.map((t) => ({
      minVolume: t.minVolume,
      maxVolume: t.maxVolume,
      ratePercent: t.ratePercent,
    })),
  };
};

/** One-line human summary, e.g. "Flat 10%" or "Tiers (ILS): 20% up to ₪2000, 10% above". */
export const describeSchedule = (schedule) => {
  if (!schedule) return '-';
  if (schedule.mode !== 'TIERED') return `Flat ${schedule.flatRatePercent}%`;
  const sym = getCurrencySymbol(schedule.tierCurrencyCode);
  const parts = (schedule.tiers || []).map((t) => (t.maxVolume === null || t.maxVolume === undefined
    ? `${t.ratePercent}% from ${sym}${t.minVolume}`
    : `${t.ratePercent}% for ${sym}${t.minVolume}-${sym}${t.maxVolume}`));
  const other = isNum(schedule.flatRatePercent) ? `; ${schedule.flatRatePercent}% other currencies` : '';
  return `Tiers (${schedule.tierCurrencyCode}): ${parts.join(', ')}${other}`;
};

/**
 * Flat / Volume-tiers editor shared by the default-commission page and the per-store section.
 * Controlled: `value` is a draft from {@link draftFromSchedule}; validate with {@link validateDraft}.
 */
const CommissionScheduleEditor = ({ value, onChange, flatRequired = false, disabled = false }) => {
  const { flatError } = validateDraft(value, { flatRequired });

  const setMode = (_, mode) => {
    if (!mode || mode === value.mode) return;
    if (mode === 'TIERED' && value.tiers.length === 0) {
      const seeded = newTierTable(value.tierCurrencyCode || 'ILS');
      onChange({ ...value, mode, ...seeded });
    } else {
      onChange({ ...value, mode });
    }
  };

  const flatLabel = value.mode === 'FLAT'
    ? 'Commission rate'
    : `Flat rate for stores in other currencies${flatRequired ? '' : ' (optional)'}`;

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={value.mode}
        onChange={setMode}
        disabled={disabled}
        aria-label="Commission type"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="FLAT">Flat</ToggleButton>
        <ToggleButton value="TIERED">Volume tiers</ToggleButton>
      </ToggleButtonGroup>

      {value.mode === 'TIERED' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The rate applies to the whole month, based on the previous month&apos;s sales volume.
          </Typography>
          <TierTableEditor
            value={{ tierCurrencyCode: value.tierCurrencyCode, tiers: value.tiers }}
            onChange={(t) => onChange({ ...value, ...t })}
            disabled={disabled}
          />
        </Box>
      )}

      <TextField
        size="small"
        type="number"
        label={flatLabel}
        inputProps={{ 'aria-label': flatLabel, min: 0, max: 100, step: 'any' }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        value={value.flatRatePercent}
        onChange={(e) => {
          const raw = e.target.value;
          const n = Number(raw);
          onChange({ ...value, flatRatePercent: raw === '' || !Number.isFinite(n) ? '' : n });
        }}
        disabled={disabled}
        error={!!flatError}
        helperText={flatError || (value.mode === 'TIERED'
          ? 'Used for stores whose currency differs from the tier currency'
          : ' ')}
        sx={{ minWidth: 320 }}
      />
    </Box>
  );
};

export default CommissionScheduleEditor;
